import { PartialNullable } from "../../../../../Extensions";
import { $$, IJNativeEvent } from "../../../../../JNative";
import { PermissionCacheService } from "../../../../../PermissionCacheService";
import { PropertyChange } from "../../../../../PropertyChange";
import { Property, PropertyInt32, PropertyString } from "../../../../../RadObject";
import { ServiceManager } from "../../../../../ServiceManager";
import { ApplicationDataService } from "../../../../../Ui/ApplicationDataService";
import { Evaluator } from "../../../SeriouslyRAD.Framework/Evaluation/Evaluator";
import { EvaluatorPropertyValues, IEvaluator } from "../../../SeriouslyRAD.Framework/Evaluation/IEvaluator";
import { NAMESPACE } from "./_Namespace";

export type PermissionEvaluatorPropertyValues =
    EvaluatorPropertyValues
    & {
        entityName: string;
        permission: number;
    };

export type PermissionEvaluatorProperties = {
    [name in keyof PermissionEvaluatorPropertyValues]: Property<PermissionEvaluatorPropertyValues[name]>
};

export interface IPermissionEvaluator extends IEvaluator, PermissionEvaluatorProperties
{
}

export class PermissionEvaluator extends Evaluator<PermissionEvaluatorPropertyValues> implements IPermissionEvaluator
{
    // #region Static

    public static override classNamespace = NAMESPACE + "PermissionEvaluator";
    public static override className = "PermissionEvaluator";

    // #region Properties

    public static permissionLevels: { [name: string]: number | undefined } = {};

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public entityName!: PropertyString;
    public permission!: PropertyInt32;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PermissionEvaluatorPropertyValues>)
    {
        super(propertyValues);

        $$(document).on("SeriouslyRAD.Application:Authenticated", { self: this }, this.onAuthenticated);
        // this causes an exception on log out - commented out for now (this is the least important of these scenarios)
        //$$(document).on("SeriouslyRAD.Application:Unauthenticated", { self: this }, this.onUnauthenticatedChanged);
        $$(document).on("SeriouslyRAD.PermissionCache:PermissionsChanged", { self: this }, this.onPermissionsChanged);
    }

    // #endregion

    // #region Protected Methods

    protected _eChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entityNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _permissionChanged(change: PropertyChange<number>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PermissionEvaluatorPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.entityName = new PropertyString(this, propertyValues.entityName, this._entityNameChanged);
        this.permission = new PropertyInt32(this, propertyValues.permission, this._permissionChanged);
    }

    public override clone(): this
    {
        const
            evaluator = super.clone() as this;

        evaluator.entityName.set(this.entityName.get());
        evaluator.permission.set(this.permission.get());

        return evaluator;
    }
    public override evaluate()
    {
        const
            permissionCache = ServiceManager.getOptional(PermissionCacheService),
            value =
                permissionCache && ServiceManager.getOptional(ApplicationDataService)?.authenticated.get()
                    ? permissionCache.hasPermissionForEntity(this.entityName.get(), this.permission.get())
                    : false;

        this.value.set(value);

        return value;
    }

    // #endregion

    // #region Event Handlers

    protected onAuthenticated(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.evaluate();
    }
    protected onPermissionsChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.evaluate();
    }
    protected onUnauthenticated(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.evaluate();
    }

    // #endregion

    // #endregion
}

